import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
import { supabase } from "./supabaseClient";
import PageLoader from "./components/PageLoader";
import FormDraftPersistence from "./components/FormDraftPersistence";
import IntranetAlertBars from "./components/IntranetAlertBars";
import SiteFeatureRouteGuard from "./components/SiteFeatureRouteGuard";
import OperationalModeGuard from "./components/OperationalModeGuard";
import { FeatureFlagsProvider } from "./contexts/FeatureFlagsContext";
import { isDeveloper } from "./utils/developer";
import { canBypassMaintenance, isMaintenanceOwner } from "./utils/maintenance";

/* Public pages */
import Maintenance from "./pages/Maintenance";
import Login from "./pages/Login";
import NotFound from "./pages/404";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import GDPR from "./pages/GDPR";
import ForgotPassword from "./pages/ForgotPassword";
import ChangeTemporaryPassword from "./pages/ChangeTemporaryPassword";
import PublicHome from "./pages/public/Home";
import PublicShifts from "./pages/public/Shifts";
import PublicVacancies from "./pages/public/Vacancies";
import PublicVacancy from "./pages/public/Vacancy";
import PublicMyApplications from "./pages/public/MyApplications";
import PatientPortal from "./pages/public/PatientPortal";

/* Intranet */
import IntranetPortal from "./pages/intranet/Portal";
import IntranetDatix from "./pages/intranet/Datix";
import IntranetProfilePage from "./pages/intranet/Profile";
import IntranetCalendar from "./pages/intranet/Calendar";
import IntranetSchedule from "./pages/intranet/Schedule";
import LeaveRequestPage from "./pages/intranet/LeaveRequest";
import News2 from "./pages/intranet/News2";
import IntranetResources from "./pages/intranet/Resources";
import IntranetDrugMatrix from "./pages/intranet/DrugMatrix";
import IntranetDrugMatrixDetail from "./pages/intranet/DrugMatrixDetail";
import IntranetCertificates from "./pages/intranet/Certificates";
import CertificatePdf from "./pages/intranet/CertificatePdf";
import IntranetVacancy from "./pages/intranet/Vacancy";
import IntranetVacancies from "./pages/intranet/Vacancies";
import IntranetComplaints from "./pages/intranet/Complaint";
import Timesheet from "./pages/intranet/Timesheet";
import IntranetOperations from "./pages/intranet/Operations";
import IntranetELearning from "./pages/intranet/ELearning";
import IntranetCompetencies from "./pages/intranet/Competencies";
import StaffHub from "./pages/intranet/StaffHub";
import HelpCentre from "./pages/intranet/HelpCentre";
import HelpAssistantWidget from "./components/HelpAssistantWidget";

/* eCAMIS */
import IntranetEcamisIndex from "./pages/intranet/eCAMIS/Index";
import CreatePatient from "./pages/intranet/eCAMIS/Create";
import CreateAppointment from "./pages/intranet/eCAMIS/CreateAppointment";
import IntranetAppointments from "./pages/intranet/eCAMIS/Appointments";
import IntranetAppointmentPage from "./pages/intranet/eCAMIS/AppointmentPage";
import IntranetPatientRecord from "./pages/intranet/eCAMIS/PatientRecord";
import IntranetPatientLookup from "./pages/intranet/eCAMIS/Lookup";
import IntranetPatientOverview from "./pages/intranet/eCAMIS/PatientOverview";
import ClinicalWorklist from "./pages/intranet/eCAMIS/ClinicalWorklist";
import RadiologyStudy from "./pages/intranet/eCAMIS/RadiologyStudy";
import CheckInQueue from "./pages/intranet/eCAMIS/CheckInQueue";
import IntranetShiftPage from "./pages/intranet/eCAMIS/ShiftPage";

/* Management */
import IntranetManagementPortal from "./pages/intranet/management/Portal";
import IntranetManagementProfile from "./pages/intranet/management/Profile";
import IntranetManagementEmail from "./pages/intranet/management/Email";
import IntranetManagementComplaints from "./pages/intranet/management/Complaints";
import ManagementTimesheet from "./pages/intranet/management/Timesheet";
import ManagementVacancies from "./pages/intranet/management/Vacancies";
import ManagementVacancyPage from "./pages/intranet/management/VacanciesPage";
import ManagementVacancyApplications from "./pages/intranet/management/VacancyApplications";
import ManagementDisciplinary from "./pages/intranet/management/Disciplinaries";
import ManagementAudit from "./pages/intranet/management/AuditLog";
import ManagementMaintenance from "./pages/intranet/management/Maintenance";
import ManagementELearning from "./pages/intranet/management/ELearning";
import ManagementCompetencies from "./pages/intranet/management/Competencies";
import ManagementWorkforceHub from "./pages/intranet/management/WorkforceHub";
import ManagementHelpCentre from "./pages/intranet/management/HelpCentre";
import ManagementDocuments from "./pages/intranet/management/Documents";
import ManagementDrugMatrix from "./pages/intranet/management/DrugMatrix";
import ManagementCertificates from "./pages/intranet/management/Certificates";
import DeveloperControls from "./pages/intranet/developer/Controls";
import DeveloperAssistant from "./pages/intranet/developer/Assistant";

/* Ward management */
import Wards from "./pages/intranet/ward/Wards";
import WardManage from "./pages/intranet/ward/WardManage";

const LAST_ROUTE_KEY = "pguh:lastRoute";
const LAST_EXACT_ROUTE_KEY = "pguh:lastExactRoute";

function isGenericIntranetRoute(value) {
  const route = String(value || "");
  const pathname = route.split(/[?#]/, 1)[0];
  return pathname === "/intranet" || pathname === "/intranet/";
}

function safeLastRoute() {
  try {
    const stored = localStorage.getItem(LAST_ROUTE_KEY);

    return stored &&
      stored.startsWith("/intranet/") &&
      !isGenericIntranetRoute(stored)
      ? stored
      : "/intranet/portal";
  } catch {
    return "/intranet/portal";
  }
}

function RouteTracker({ session }) {
  const location = useLocation();

  useEffect(() => {
    const exactRoute = `${location.pathname}${location.search}${location.hash}`;

    const genericIntranetRoute = isGenericIntranetRoute(location.pathname);

    try {
      // Keep the exact page in localStorage so a full browser reload can
      // recover it even when the hosting layer falls back to /intranet/portal.
      if (location.pathname !== "/login" && !genericIntranetRoute) {
        localStorage.setItem(LAST_EXACT_ROUTE_KEY, exactRoute);
      }

      if (
        session &&
        location.pathname.startsWith("/intranet/") &&
        !genericIntranetRoute
      ) {
        localStorage.setItem(LAST_ROUTE_KEY, exactRoute);
      }
    } catch (error) {
      console.warn("Unable to save the current route:", error);
    }
  }, [location.hash, location.pathname, location.search, session]);

  useEffect(() => {
    const main = document.getElementById("main-content");
    if (main) main.focus({ preventScroll: true });
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [location.pathname]);

  return null;
}

function RequireAuth({ session, profile, children }) {
  const location = useLocation();
  if (!session) {
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: `${location.pathname}${location.search}` }}
      />
    );
  }
  if (profile?.position === "Patient") {
    return <Navigate to="/patient-portal" replace />;
  }

  return children;
}

function RequireStaff({ session, profile, children }) {
  const location = useLocation();

  if (!session) {
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: `${location.pathname}${location.search}` }}
      />
    );
  }

  if (!profile) {
    return <PageLoader label="Restoring your staff account" />;
  }

  if (!["Staff", "Management"].includes(profile.position)) {
    return <Navigate to="/intranet/portal" replace />;
  }

  return children;
}

function RequireManagementPortal({ session, profile, children }) {
  const location = useLocation();

  if (!session) {
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: `${location.pathname}${location.search}` }}
      />
    );
  }

  if (!profile) {
    return <PageLoader label="Restoring your management access" />;
  }

  const allowed =
    profile.position === "Management" ||
    (profile.position === "Staff" && profile.management_portal === true);

  return allowed ? children : <Navigate to="/intranet/portal" replace />;
}

function RequireManagementOnly({ session, profile, children }) {
  const location = useLocation();

  if (!session) {
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: `${location.pathname}${location.search}` }}
      />
    );
  }

  if (!profile) {
    return <PageLoader label="Restoring your management access" />;
  }

  return profile.position === "Management" ? (
    children
  ) : (
    <Navigate to="/intranet/portal" replace />
  );
}


function RequireDeveloper({ session, children }) {
  const location = useLocation();
  if (!session) {
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: `${location.pathname}${location.search}` }}
      />
    );
  }

  return isDeveloper(session) ? children : <Navigate to="/intranet/portal" replace />;
}

function RequireMaintenanceOwner({ session, children }) {
  const location = useLocation();

  if (!session) {
    return (
      <Navigate
        to="/login?maintenance=1"
        replace
        state={{ from: `${location.pathname}${location.search}` }}
      />
    );
  }

  return isMaintenanceOwner(session) ? (
    children
  ) : (
    <Navigate to="/intranet/management/portal" replace />
  );
}

export default function App() {
  const location = useLocation();
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [maintenanceLoading, setMaintenanceLoading] = useState(true);
  const [maintenance, setMaintenance] = useState({
    enabled: false,
    message: "",
    updated_at: null,
  });
  const profileRequestRef = useRef(0);

  const loadProfile = useCallback(async (nextSession) => {
    const requestId = ++profileRequestRef.current;
    if (!nextSession) {
      if (requestId === profileRequestRef.current) setProfile(null);
      return null;
    }

    const { data, error } = await supabase
      .from("profiles")
      .select("*")
      .eq("id", nextSession.user.id)
      .maybeSingle();

    if (requestId !== profileRequestRef.current) return null;

    if (error) {
      console.error("Unable to load the signed-in profile:", error);
      setProfile(null);
      return null;
    }

    setProfile(data || null);
    return data || null;
  }, []);

  useEffect(() => {
    let mounted = true;
    let bootstrapped = false;

    const initialise = async () => {
      const { data, error } = await supabase.auth.getSession();
      if (!mounted) return;

      if (error) {
        console.error("Unable to restore the Supabase session:", error);
      }

      const restoredSession = data?.session || null;
      setSession(restoredSession);
      await loadProfile(restoredSession);

      if (mounted) {
        bootstrapped = true;
        setLoading(false);
      }
    };

    initialise();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, nextSession) => {
      if (!mounted) return;

      setSession(nextSession || null);

      if (!nextSession) {
        profileRequestRef.current += 1;
        setProfile(null);
        return;
      }

      // getSession() + loadProfile() owns the initial boot. Ignoring the
      // duplicate initial SIGNED_IN event prevents a second profile request
      // from invalidating the first one and temporarily failing route guards.
      if (!bootstrapped) return;

      if (event === "TOKEN_REFRESHED") return;

      window.setTimeout(() => {
        if (mounted) void loadProfile(nextSession);
      }, 0);
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [loadProfile]);

  useEffect(() => {
    if (!session?.user?.id) return undefined;

    let active = true;

    const shouldRevoke = (row) => {
      if (!row?.force_logout_at) return false;
      const forcedAt = new Date(row.force_logout_at).getTime();
      const signedInAt = new Date(session.user.last_sign_in_at || 0).getTime();
      return Number.isFinite(forcedAt) && forcedAt > 0 && forcedAt >= signedInAt;
    };

    const applyAccountPolicy = async (row) => {
      if (!active || !row) return;

      if (row.suspended === true && row.suspended_until) {
        const suspensionEnd = new Date(row.suspended_until).getTime();
        if (Number.isFinite(suspensionEnd) && suspensionEnd <= Date.now()) {
          const { error } = await supabase.rpc("ruht_clear_my_expired_suspension");
          if (!error) {
            const { data: refreshed } = await supabase
              .from("profiles")
              .select("*")
              .eq("id", session.user.id)
              .maybeSingle();
            if (refreshed) row = refreshed;
          }
        }
      }

      setProfile((current) => current ? { ...current, ...row } : row);

      if (row.suspended === true) {
        try {
          await supabase.auth.signOut({ scope: "global" });
        } finally {
          window.location.assign("/login?suspended=1");
        }
        return;
      }

      if (shouldRevoke(row)) {
        try {
          await supabase.auth.signOut({ scope: "global" });
        } finally {
          window.location.assign("/login?forced=1");
        }
      }
    };

    const checkPolicy = async () => {
      const { data } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", session.user.id)
        .maybeSingle();
      if (data) await applyAccountPolicy(data);
    };

    void checkPolicy();
    const interval = window.setInterval(checkPolicy, 15000);
    const onVisible = () => { if (document.visibilityState === "visible") void checkPolicy(); };
    document.addEventListener("visibilitychange", onVisible);

    const channel = supabase
      .channel(`ruht-account-policy-${session.user.id}`)
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "profiles", filter: `id=eq.${session.user.id}` },
        (payload) => void applyAccountPolicy(payload.new),
      )
      .subscribe();

    return () => {
      active = false;
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
      supabase.removeChannel(channel);
    };
  }, [session?.user?.id, session?.user?.last_sign_in_at]);

  useEffect(() => {
    let mounted = true;

    const applyMaintenance = (row) => {
      if (!mounted) return;
      setMaintenance({
        enabled: row?.enabled === true,
        message: String(row?.message || ""),
        updated_at: row?.updated_at || null,
      });
    };

    const loadMaintenance = async () => {
      const { data, error } = await supabase
        .from("site_maintenance")
        .select("enabled, message, updated_at")
        .eq("id", 1)
        .maybeSingle();

      if (!mounted) return;

      if (error) {
        // Keep the site usable before the maintenance migration is installed.
        console.warn("Unable to load site maintenance status:", error.message || error);
        applyMaintenance(null);
      } else {
        applyMaintenance(data);
      }

      setMaintenanceLoading(false);
    };

    void loadMaintenance();

    const onLocalMaintenanceChange = (event) => {
      applyMaintenance(event?.detail || null);
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible") void loadMaintenance();
    };

    window.addEventListener("ruht:maintenance-changed", onLocalMaintenanceChange);
    document.addEventListener("visibilitychange", onVisibility);

    const channel = supabase
      .channel("ruht-site-maintenance")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "site_maintenance",
          filter: "id=eq.1",
        },
        (payload) => applyMaintenance(payload.new || null),
      )
      .subscribe();

    return () => {
      mounted = false;
      window.removeEventListener("ruht:maintenance-changed", onLocalMaintenanceChange);
      document.removeEventListener("visibilitychange", onVisibility);
      supabase.removeChannel(channel);
    };
  }, []);

  if (loading || maintenanceLoading) return <PageLoader />;

  const bypassMaintenance = canBypassMaintenance(session, profile);
  const maintenanceLoginRoute = location.pathname === "/login";

  if (maintenance.enabled && !bypassMaintenance && !maintenanceLoginRoute) {
    return <Maintenance message={maintenance.message} />;
  }

  const sharedProps = { session, profile };

  if (
    session &&
    profile?.must_change_password === true &&
    location.pathname !== "/change-password"
  ) {
    return <Navigate to="/change-password" replace />;
  }

  return (
    <FeatureFlagsProvider>
      <OperationalModeGuard session={session} profile={profile}>
      <SiteFeatureRouteGuard session={session} profile={profile}>
        <>
          <RouteTracker session={session} />
          <FormDraftPersistence session={session} />
          {session && location.pathname.startsWith("/intranet") && <IntranetAlertBars />}
          {session && location.pathname.startsWith("/intranet") && ["Staff", "Management"].includes(profile?.position) && <HelpAssistantWidget profile={profile} />}
          <Routes>
        {/* Public */}
        <Route path="/" element={<PublicHome {...sharedProps} />} />
        <Route path="/home" element={<Navigate to="/" replace />} />
        <Route path="/shifts" element={<PublicShifts {...sharedProps} />} />
        <Route path="/vacancies" element={<PublicVacancies {...sharedProps} />} />
        <Route path="/vacancies/:id" element={<PublicVacancy {...sharedProps} />} />
        <Route path="/my-applications" element={<PublicMyApplications {...sharedProps} />} />
        <Route path="/patient-portal" element={<PatientPortal {...sharedProps} />} />
        <Route
          path="/login"
          element={
            session && (!maintenance.enabled || canBypassMaintenance(session, profile)) ? (
              <Navigate
                to={profile?.position === "Patient" ? "/patient-portal" : safeLastRoute()}
                replace
              />
            ) : (
              <Login maintenanceAdminOnly={maintenance.enabled} />
            )
          }
        />
        <Route path="/gdpr" element={<GDPR />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/maintenance" element={<Maintenance />} />
        <Route path="/reset-password" element={<ForgotPassword />} />
        <Route path="/change-password" element={<ChangeTemporaryPassword {...sharedProps} />} />

        {/* Intranet */}
        <Route
          path="/intranet"
          element={<Navigate to={safeLastRoute()} replace />}
        />
        <Route
          path="/intranet/portal"
          element={
            <RequireAuth {...sharedProps}>
              <IntranetPortal {...sharedProps} />
            </RequireAuth>
          }
        />
        <Route
          path="/intranet/timesheet"
          element={
            <RequireAuth {...sharedProps}>
              <Timesheet {...sharedProps} />
            </RequireAuth>
          }
        />
        <Route
          path="/intranet/datix"
          element={
            <RequireStaff {...sharedProps}>
              <IntranetDatix {...sharedProps} />
            </RequireStaff>
          }
        />
        <Route
          path="/intranet/vacancy/:id"
          element={
            <RequireStaff {...sharedProps}>
              <IntranetVacancy {...sharedProps} />
            </RequireStaff>
          }
        />
        <Route
          path="/intranet/vacancies"
          element={
            <RequireStaff {...sharedProps}>
              <IntranetVacancies {...sharedProps} />
            </RequireStaff>
          }
        />
        <Route
          path="/intranet/resources"
          element={
            <RequireStaff {...sharedProps}>
              <IntranetResources {...sharedProps} />
            </RequireStaff>
          }
        />
        <Route
          path="/intranet/drug-matrix"
          element={
            <RequireStaff {...sharedProps}>
              <IntranetDrugMatrix {...sharedProps} />
            </RequireStaff>
          }
        />
        <Route
          path="/intranet/drug-matrix/:slug"
          element={
            <RequireStaff {...sharedProps}>
              <IntranetDrugMatrixDetail {...sharedProps} />
            </RequireStaff>
          }
        />
        <Route
          path="/intranet/certificates"
          element={
            <RequireStaff {...sharedProps}>
              <IntranetCertificates {...sharedProps} />
            </RequireStaff>
          }
        />
        <Route
          path="/intranet/certificates/:certificateId/pdf"
          element={
            <RequireStaff {...sharedProps}>
              <CertificatePdf {...sharedProps} />
            </RequireStaff>
          }
        />
        <Route
          path="/intranet/complaint"
          element={
            <RequireStaff {...sharedProps}>
              <IntranetComplaints {...sharedProps} />
            </RequireStaff>
          }
        />
        <Route
          path="/intranet/leave-requests"
          element={
            <RequireStaff {...sharedProps}>
              <LeaveRequestPage {...sharedProps} />
            </RequireStaff>
          }
        />
        <Route
          path="/intranet/profile-settings"
          element={
            <RequireAuth {...sharedProps}>
              <IntranetProfilePage {...sharedProps} />
            </RequireAuth>
          }
        />
        <Route
          path="/intranet/calendar"
          element={
            <RequireStaff {...sharedProps}>
              <IntranetCalendar {...sharedProps} />
            </RequireStaff>
          }
        />
        <Route
          path="/intranet/myschedule"
          element={
            <RequireStaff {...sharedProps}>
              <IntranetSchedule {...sharedProps} />
            </RequireStaff>
          }
        />
        <Route
          path="/intranet/news2-calculator"
          element={
            <RequireStaff {...sharedProps}>
              <News2 {...sharedProps} />
            </RequireStaff>
          }
        />
        <Route
          path="/intranet/operations"
          element={
            <RequireStaff {...sharedProps}>
              <IntranetOperations {...sharedProps} />
            </RequireStaff>
          }
        />
        <Route
          path="/intranet/e-learning"
          element={
            <RequireStaff {...sharedProps}>
              <IntranetELearning {...sharedProps} />
            </RequireStaff>
          }
        />
        <Route
          path="/intranet/competencies"
          element={
            <RequireStaff {...sharedProps}>
              <IntranetCompetencies {...sharedProps} />
            </RequireStaff>
          }
        />
        <Route
          path="/intranet/my-workplace"
          element={
            <RequireStaff {...sharedProps}>
              <StaffHub {...sharedProps} />
            </RequireStaff>
          }
        />
        <Route
          path="/intranet/help-centre"
          element={
            <RequireStaff {...sharedProps}>
              <HelpCentre {...sharedProps} />
            </RequireStaff>
          }
        />

        {/* eCAMIS */}
        <Route
          path="/intranet/ecamis/shift/:shiftId"
          element={
            <RequireStaff {...sharedProps}>
              <IntranetShiftPage {...sharedProps} />
            </RequireStaff>
          }
        />
        <Route
          path="/intranet/ecamis/index"
          element={
            <RequireStaff {...sharedProps}>
              <IntranetEcamisIndex {...sharedProps} />
            </RequireStaff>
          }
        />
        <Route
          path="/intranet/ecamis/lookup"
          element={
            <RequireStaff {...sharedProps}>
              <IntranetPatientLookup {...sharedProps} />
            </RequireStaff>
          }
        />
        <Route
          path="/intranet/ecamis/create"
          element={
            <RequireStaff {...sharedProps}>
              <CreatePatient {...sharedProps} />
            </RequireStaff>
          }
        />
        <Route
          path="/intranet/ecamis/new-appointment"
          element={
            <RequireStaff {...sharedProps}>
              <CreateAppointment {...sharedProps} />
            </RequireStaff>
          }
        />
        <Route
          path="/intranet/ecamis/appointments"
          element={
            <RequireStaff {...sharedProps}>
              <IntranetAppointments {...sharedProps} />
            </RequireStaff>
          }
        />
        <Route
          path="/intranet/ecamis/clinical-worklist"
          element={
            <RequireStaff {...sharedProps}>
              <ClinicalWorklist {...sharedProps} />
            </RequireStaff>
          }
        />
        <Route
          path="/intranet/ecamis/imaging/:studyId"
          element={
            <RequireStaff {...sharedProps}>
              <RadiologyStudy {...sharedProps} />
            </RequireStaff>
          }
        />
        <Route
          path="/intranet/ecamis/check-in-queue"
          element={
            <RequireStaff {...sharedProps}>
              <CheckInQueue {...sharedProps} />
            </RequireStaff>
          }
        />
        <Route
          path="/intranet/ecamis/appointment/:id"
          element={
            <RequireStaff {...sharedProps}>
              <IntranetAppointmentPage {...sharedProps} />
            </RequireStaff>
          }
        />
        <Route
          path="/intranet/ecamis/patient-overview/:accountId"
          element={
            <RequireStaff session={session} profile={profile}>
              <IntranetPatientOverview {...sharedProps} />
            </RequireStaff>
          }
        />

        <Route
          path="/intranet/ecamis/patient/:id"
          element={
            <RequireStaff {...sharedProps}>
              <IntranetPatientRecord {...sharedProps} />
            </RequireStaff>
          }
        />

        {/* Management */}
        <Route
          path="/intranet/management/portal"
          element={
            <RequireManagementPortal {...sharedProps}>
              <IntranetManagementPortal {...sharedProps} />
            </RequireManagementPortal>
          }
        />
        <Route
          path="/intranet/management/profiles"
          element={
            <RequireManagementOnly {...sharedProps}>
              <IntranetManagementProfile {...sharedProps} />
            </RequireManagementOnly>
          }
        />
        <Route
          path="/intranet/management/email"
          element={
            <RequireManagementOnly {...sharedProps}>
              <IntranetManagementEmail {...sharedProps} />
            </RequireManagementOnly>
          }
        />
        <Route
          path="/intranet/management/complaints"
          element={
            <RequireManagementOnly {...sharedProps}>
              <IntranetManagementComplaints {...sharedProps} />
            </RequireManagementOnly>
          }
        />
        <Route
          path="/intranet/management/e-learning"
          element={
            <RequireManagementOnly {...sharedProps}>
              <ManagementELearning {...sharedProps} />
            </RequireManagementOnly>
          }
        />
        <Route
          path="/intranet/management/competencies"
          element={
            <RequireManagementOnly {...sharedProps}>
              <ManagementCompetencies {...sharedProps} />
            </RequireManagementOnly>
          }
        />
        <Route
          path="/intranet/management/workforce"
          element={
            <RequireManagementOnly {...sharedProps}>
              <ManagementWorkforceHub {...sharedProps} />
            </RequireManagementOnly>
          }
        />
        <Route
          path="/intranet/management/help-centre"
          element={
            <RequireManagementOnly {...sharedProps}>
              <ManagementHelpCentre {...sharedProps} />
            </RequireManagementOnly>
          }
        />
        <Route
          path="/intranet/management/documents"
          element={
            <RequireManagementOnly {...sharedProps}>
              <ManagementDocuments {...sharedProps} />
            </RequireManagementOnly>
          }
        />
        <Route
          path="/intranet/management/drug-matrix"
          element={
            <RequireManagementOnly {...sharedProps}>
              <ManagementDrugMatrix {...sharedProps} />
            </RequireManagementOnly>
          }
        />
        <Route
          path="/intranet/management/certificates"
          element={
            <RequireManagementOnly {...sharedProps}>
              <ManagementCertificates {...sharedProps} />
            </RequireManagementOnly>
          }
        />
        <Route
          path="/intranet/management/timesheet"
          element={
            <RequireManagementOnly {...sharedProps}>
              <ManagementTimesheet {...sharedProps} />
            </RequireManagementOnly>
          }
        />
        <Route
          path="/intranet/management/vacancies"
          element={
            <RequireManagementOnly {...sharedProps}>
              <ManagementVacancies {...sharedProps} />
            </RequireManagementOnly>
          }
        />
        <Route
          path="/intranet/management/vacancies/:id/applications"
          element={
            <RequireManagementOnly {...sharedProps}>
              <ManagementVacancyApplications {...sharedProps} />
            </RequireManagementOnly>
          }
        />
        <Route
          path="/intranet/management/vacancies/:id"
          element={
            <RequireManagementOnly {...sharedProps}>
              <ManagementVacancyPage {...sharedProps} />
            </RequireManagementOnly>
          }
        />
        <Route
          path="/intranet/disciplinaries/:profileId"
          element={
            <RequireManagementOnly {...sharedProps}>
              <ManagementDisciplinary {...sharedProps} />
            </RequireManagementOnly>
          }
        />
        <Route
          path="/intranet/management/audit-log"
          element={
            <RequireManagementOnly {...sharedProps}>
              <ManagementAudit {...sharedProps} />
            </RequireManagementOnly>
          }
        />
        <Route
          path="/intranet/management/maintenance"
          element={
            <RequireMaintenanceOwner session={session}>
              <ManagementMaintenance {...sharedProps} />
            </RequireMaintenanceOwner>
          }
        />

        {/* Ward management */}
        <Route
          path="/intranet/wards"
          element={
            <RequireStaff {...sharedProps}>
              <Wards {...sharedProps} />
            </RequireStaff>
          }
        />
        <Route
          path="/intranet/wards/:ward"
          element={
            <RequireStaff {...sharedProps}>
              <WardManage {...sharedProps} />
            </RequireStaff>
          }
        />

            <Route
              path="/intranet/developer/controls"
              element={
                <RequireDeveloper session={session}>
                  <DeveloperControls {...sharedProps} />
                </RequireDeveloper>
              }
            />
            <Route
              path="/intranet/developer/assistant"
              element={
                <RequireDeveloper session={session}>
                  <DeveloperAssistant {...sharedProps} />
                </RequireDeveloper>
              }
            />

            <Route path="*" element={<NotFound />} />
          </Routes>
        </>
      </SiteFeatureRouteGuard>
      </OperationalModeGuard>
    </FeatureFlagsProvider>
  );
}
